// DocumentsTable component for managing experience documents
import { FormikProps } from "formik";
import { Dispatch, SetStateAction, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FreelancerProfileProfessionalExperienceDocument } from "../../../../../models/FreelancerProfileProfessionalExperienceDocument";
import {
	FreelancerOnboardingMyPresentationExperienceViewModel,
	FreelancerOnboardingMyPresentationStepViewModel
} from "../../../../../ViewModels/FreelancerOnboardingMyPresentationStepViewModel";
import { convertFileToBase64 } from "../../../../../utils/file";
import DocumentTable from "../../../../common/DocumentTable";
import { Typography } from "@mui/material";

interface DocumentsTableProps {
	readonly experienceIndex: number;
	readonly experience: FreelancerOnboardingMyPresentationExperienceViewModel;
	readonly formikRef: React.MutableRefObject<
		FormikProps<FreelancerOnboardingMyPresentationStepViewModel>
	>;
	readonly hasFieldError: (fieldPath: string) => boolean;
	readonly getFieldError: (fieldPath: string) => string | undefined;
	readonly activeTable: "documents" | "urls" | null;
	readonly setIsAddingDocument: Dispatch<SetStateAction<boolean>>;
	readonly setAddingDocument: Dispatch<
		SetStateAction<FreelancerProfileProfessionalExperienceDocument | null>
	>;
	readonly addingDocument: FreelancerProfileProfessionalExperienceDocument | null;
	readonly isAddingDocument: boolean;
	readonly setErrorDialog: Dispatch<
		SetStateAction<{
			open: boolean;
			title: string;
			message: string;
		}>
	>;
}

export default function DocumentsTable({
	experienceIndex,
	experience,
	formikRef,
	hasFieldError,
	getFieldError,
	activeTable,
	setIsAddingDocument,
	setAddingDocument,
	addingDocument,
	isAddingDocument,
	setErrorDialog
}: DocumentsTableProps) {
	const { t } = useTranslation();

	// Reset internal state when this table becomes inactive
	useEffect(() => {
		if (activeTable !== "documents") {
			setIsAddingDocument(false);
			setAddingDocument(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTable]);

	// Function to show error dialog
	const showErrorDialog = useCallback((title: string, message: string) => {
		setErrorDialog({
			open: true,
			title,
			message
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleAddNewDocument = useCallback(() => {
		// Check document count limit (5 documents max)
		const currentDocuments =
			formikRef.current.values.professionalExperiences[experienceIndex]?.documents || [];
		if (currentDocuments.length >= 5) {
			showErrorDialog(
				t("freelancerOnboardingMyPresentation.validation.documentsMaxCountTitle"),
				t("freelancerOnboardingMyPresentation.validation.documentsMaxCount")
			);
			return;
		}

		setIsAddingDocument(true);
		setAddingDocument({
			name: "",
			urlPath: "",
			freelancerProfileProfessionalExperienceId: 0
		} as FreelancerProfileProfessionalExperienceDocument);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [experienceIndex, formikRef, showErrorDialog, t]);

	const handleConfirmAddDocument = useCallback(() => {
		if (addingDocument?.name) {
			const currentDocuments =
				formikRef.current.values.professionalExperiences[experienceIndex]?.documents ||
				[];
			const updatedDocuments = [...currentDocuments, addingDocument];
			formikRef.current.setFieldValue(
				`professionalExperiences.${experienceIndex}.documents`,
				updatedDocuments
			);
			setAddingDocument(null);
			setIsAddingDocument(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addingDocument, experienceIndex, formikRef]);

	const handleCancelAddDocument = useCallback(() => {
		setAddingDocument(null);
		setIsAddingDocument(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const removeDocument = useCallback(
		(documentIndex: number) => {
			const currentExperience =
				formikRef.current.values.professionalExperiences[experienceIndex];
			const updatedDocuments = currentExperience.documents.filter(
				(_, i: number) => i !== documentIndex
			);
			formikRef.current.setFieldValue(
				`professionalExperiences.${experienceIndex}.documents`,
				updatedDocuments
			);
		},
		[experienceIndex, formikRef]
	);

	// Helper function to handle file upload
	const handleFileUpload = async (file: File, experienceIndex: number) => {
		try {
			// Check file size (5MB limit)
			if (file.size > 5 * 1024 * 1024) {
				showErrorDialog(
					t("freelancerOnboardingMyPresentation.validation.documentMaxSizeTitle"),
					t("freelancerOnboardingMyPresentation.validation.documentMaxSize")
				);
				return;
			}

			// Check file type
			const allowedTypes = [
				"application/pdf",
				"application/msword",
				"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
				"application/vnd.ms-excel",
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				"image/jpeg",
				"image/jpg",
				"image/png"
			];
			if (!allowedTypes.includes(file.type)) {
				showErrorDialog(
					t("freelancerOnboardingMyPresentation.validation.documentInvalidTypeTitle"),
					t("freelancerOnboardingMyPresentation.validation.documentInvalidType")
				);
				return;
			}

			// Check document count limit (5 documents max)
			const currentDocuments =
				formikRef.current.values.professionalExperiences[experienceIndex]?.documents ||
				[];
			if (currentDocuments.length >= 5) {
				showErrorDialog(
					t("freelancerOnboardingMyPresentation.validation.documentsMaxCountTitle"),
					t("freelancerOnboardingMyPresentation.validation.documentsMaxCount")
				);
				return;
			}

			// Check for duplicate document names
			const isDuplicateName = currentDocuments.some(
				doc => doc.name.toLowerCase() === file.name.toLowerCase()
			);

			if (isDuplicateName) {
				showErrorDialog(
					t("freelancerOnboardingMyPresentation.validation.documentDuplicateTitle"),
					t("freelancerOnboardingMyPresentation.validation.documentDuplicate")
				);
				return;
			}

			const base64Content = await convertFileToBase64(file);
			setAddingDocument({
				name: file.name,
				urlPath: URL.createObjectURL(file),
				content: base64Content
			} as FreelancerProfileProfessionalExperienceDocument);
		} catch (error) {
			console.error("Error converting file to base64:", error);
		}
	};

	return (
		<>
			<DocumentTable
				documents={experience.documents || []}
				isVisible={activeTable === "documents"}
				isAddingDocument={isAddingDocument}
				addingDocument={addingDocument}
				onAddNewDocument={handleAddNewDocument}
				onFileUpload={(file: File) => handleFileUpload(file, experienceIndex)}
				onConfirmAddDocument={handleConfirmAddDocument}
				onCancelAddDocument={handleCancelAddDocument}
				onRemoveDocument={removeDocument}
				onDocumentChange={(doc: any) => setAddingDocument(prev => ({ ...prev!, ...doc }))}
				showTypeColumn={false}
				documentTypes={[]}
				tableWidth="100%"
				useExperiencesContainer={true}
			/>
			{/* Documents validation error */}
			{hasFieldError(`professionalExperiences.${experienceIndex}.documents`) && (
				<Typography color="error" variant="body2" sx={{ mt: 1, display: "block" }}>
					{getFieldError(`professionalExperiences.${experienceIndex}.documents`)}
				</Typography>
			)}
		</>
	);
}
